/* ============================================================================
   Meditation (Breath Awareness / Dhyana) — SHARED CONTENT
   Read by the tutorial slide (10b_meditation.tsx), its portrait text layout
   (MeditationMobile.tsx) and the after-login practice panel
   (10b_meditation_body.tsx, section `body`). Edit copy ONLY here — English and
   German side by side; the German object must have the same shape.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accentMed = "#4bb8b0";

export interface Seg { t: string; b?: boolean; em?: boolean; hl?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Stillness",
    breadcrumbAccent: "Resting in the natural breath",
    headerTitle: "Meditation",
    sanskrit: "ध्यान",
    titlePre: "Breath Awareness ",
    titleAccentWord: "Dhyana",
    titlePost: "",
    techniqueLabel: "Technique",
    stillnessLabel: "A Moment of Stillness",
    guideSubtitle: "Breath awareness · observe the natural breath — ~2 min",
  },
  technique: [
    { n: "01", title: "Settle & Close the Eyes",       desc: "Having calmed the breath through pranayama, let the body be still and gently close the eyes." },
    { n: "02", title: "Let the Breath Be Natural",     desc: "Release all control. No shaping, no counting — allow the breath to find its own easy rhythm." },
    { n: "03", title: "Rest Attention on the Breath",  desc: "Feel the cool inflow and the soft release. Simply observe each breath as it comes and goes." },
    { n: "04", title: "Return, Gently",                desc: "When the mind wanders — and it will — notice without judgement and come back to the breath." },
    { n: "05", title: "Keep the Body Relaxed",         desc: "Let the shoulders, jaw, and hands soften. Stillness in the body supports stillness in the mind." },
    { n: "06", title: "Drifting Is Normal",            desc: "The mind will wander many times — this is expected. The practice is simply noticing and returning." },
  ] as { n: string; title: string; desc: string }[],
  intro: [
    { t: "A quiet bridge between active breathwork and deep rest — a moment to simply " },
    { t: "observe", hl: true },
    { t: " the breath and let the mind grow " },
    { t: "calm and spacious", hl: true },
    { t: " before Yoga Nidra." },
  ] as Seg[],
  stillness: [
    { t: "Two unhurried minutes of resting attention — " },
    { t: "the quiet threshold before the dream.", b: true },
  ] as Seg[],
  /** After-login practice panel (10b_meditation_body.tsx). */
  body: {
    titlePre: "Breath ",
    titleAccentWord: "Awareness",
    subtitle: "Dhyana · A quiet bridge from breath to deep rest",
    stillnessLabel: "A moment of stillness",
    stillness: [
      { t: "Having settled the breath through pranayama, we now simply " },
      { t: "observe", b: true },
      { t: " — letting the breath return to its own natural rhythm. Nothing to control, nothing to count. Just gentle, resting attention." },
    ] as Seg[],
    howLabel: "How to rest here",
    steps: [
      { step: "Settle & close the eyes",      desc: "Having calmed the breath through pranayama, let the body be still and gently close the eyes." },
      { step: "Let the breath be natural",    desc: "Release all control. No shaping, no counting — allow it to flow on its own." },
      { step: "Rest attention on the breath", desc: "Feel the cool inflow and the soft release. Simply observe each breath as it comes and goes." },
      { step: "Return, gently",               desc: "When the mind wanders — and it will — notice without judgement and come back to the breath." },
      { step: "Keep the body relaxed",        desc: "Let the shoulders, jaw, and hands soften. Stillness in the body supports stillness in the mind." },
      { step: "Drifting is normal",           desc: "The mind will wander many times — this is expected. Simply notice, and return." },
    ] as { step: string; desc: string }[],
    durationLabel: "Duration",
    duration: [
      { t: "A short " },
      { t: "two-minute", b: true },
      { t: " settling. Let the mind grow quiet and spacious before we move into Yoga Nidra." },
    ] as Seg[],
  },
  /** Text drawn on the breath-orb canvas. */
  canvas: {
    inhale: "INHALE",
    exhale: "EXHALE",
    guideLine: "observe the natural breath",
    breath: (n: number, of: number) => `breath ${n} · of ${of}`,
    pressPlay: "press ▶ to begin",
  },
};

export type MeditationContent = typeof en;

const de: MeditationContent = {
  meta: {
    breadcrumbPre: "Stille",
    breadcrumbAccent: "Ruhen im natürlichen Atem",
    headerTitle: "Meditation",
    sanskrit: "ध्यान",
    titlePre: "Atembewusstsein ",
    titleAccentWord: "Dhyana",
    titlePost: "",
    techniqueLabel: "Technik",
    stillnessLabel: "Ein Moment der Stille",
    guideSubtitle: "Atembewusstsein · den natürlichen Atem beobachten – ca. 2 Min.",
  },
  technique: [
    { n: "01", title: "Ankommen & Augen schließen",       desc: "Der Atem ist durch Pranayama ruhig geworden – lass den Körper still werden und schließe sanft die Augen." },
    { n: "02", title: "Den Atem natürlich lassen",        desc: "Gib alle Kontrolle ab. Nichts formen, nichts zählen – lass den Atem seinen eigenen, leichten Rhythmus finden." },
    { n: "03", title: "Die Aufmerksamkeit beim Atem",     desc: "Spüre das kühle Einströmen und das weiche Loslassen. Beobachte einfach jeden Atemzug, wie er kommt und geht." },
    { n: "04", title: "Sanft zurückkehren",               desc: "Wenn der Geist abschweift – und das wird er –, bemerke es ohne Urteil und kehre zum Atem zurück." },
    { n: "05", title: "Den Körper entspannt halten",      desc: "Lass Schultern, Kiefer und Hände weich werden. Stille im Körper unterstützt Stille im Geist." },
    { n: "06", title: "Abschweifen ist normal",           desc: "Der Geist wird oft abschweifen – das ist zu erwarten. Die Übung besteht einfach im Bemerken und Zurückkehren." },
  ],
  intro: [
    { t: "Eine ruhige Brücke zwischen aktiver Atemarbeit und tiefer Ruhe – ein Moment, um den Atem einfach zu " },
    { t: "beobachten", hl: true },
    { t: " und den Geist vor Yoga Nidra " },
    { t: "ruhig und weit", hl: true },
    { t: " werden zu lassen." },
  ],
  stillness: [
    { t: "Zwei ruhige Minuten wacher, ruhender Aufmerksamkeit – " },
    { t: "die stille Schwelle vor dem Traum.", b: true },
  ],
  body: {
    titlePre: "Atem",
    titleAccentWord: "bewusstsein",
    subtitle: "Dhyana · Eine ruhige Brücke vom Atem in die tiefe Ruhe",
    stillnessLabel: "Ein Moment der Stille",
    stillness: [
      { t: "Nachdem sich der Atem durch Pranayama beruhigt hat, " },
      { t: "beobachten", b: true },
      { t: " wir jetzt nur noch – und lassen den Atem zu seinem eigenen, natürlichen Rhythmus zurückfinden. Nichts zu kontrollieren, nichts zu zählen. Nur sanfte, ruhende Aufmerksamkeit." },
    ],
    howLabel: "So ruhst du hier",
    steps: [
      { step: "Ankommen & Augen schließen",   desc: "Der Atem ist durch Pranayama ruhig geworden – lass den Körper still werden und schließe sanft die Augen." },
      { step: "Den Atem natürlich lassen",    desc: "Gib alle Kontrolle ab. Nichts formen, nichts zählen – lass ihn von selbst fließen." },
      { step: "Aufmerksamkeit beim Atem",     desc: "Spüre das kühle Einströmen und das weiche Loslassen. Beobachte einfach jeden Atemzug, wie er kommt und geht." },
      { step: "Sanft zurückkehren",           desc: "Wenn der Geist abschweift – und das wird er –, bemerke es ohne Urteil und kehre zum Atem zurück." },
      { step: "Den Körper entspannt halten",  desc: "Lass Schultern, Kiefer und Hände weich werden. Stille im Körper unterstützt Stille im Geist." },
      { step: "Abschweifen ist normal",       desc: "Der Geist wird oft abschweifen – das ist zu erwarten. Bemerke es einfach und kehre zurück." },
    ],
    durationLabel: "Dauer",
    duration: [
      { t: "Ein kurzes Ankommen von " },
      { t: "zwei Minuten", b: true },
      { t: ". Lass den Geist still und weit werden, bevor wir in Yoga Nidra übergehen." },
    ],
  },
  canvas: {
    inhale: "EINATMEN",
    exhale: "AUSATMEN",
    guideLine: "den natürlichen Atem beobachten",
    breath: (n: number, of: number) => `Atemzug ${n} · von ${of}`,
    pressPlay: "▶ drücken zum Starten",
  },
};

export const meditationContent: ByLang<MeditationContent> = { en, de };
export const useMeditation = () => useL(meditationContent);
